import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Wrench,
  WifiOff,
  AlertTriangle,
  Users,
  CheckCircle,
  RefreshCw,
  Phone,
  MapPin,
  Signal,
  Router,
  XCircle,
  Eye,
} from "lucide-react";
import { PelangganFotoDialog } from "../components/common/PelangganFoto";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const TeknisiDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [fotoPel, setFotoPel] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/teknisi/dashboard`, { withCredentials: true });
      setData(res.data);
      setDismissed(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 60000);
    return () => clearInterval(timer);
  }, [fetchData]);

  const summary = data?.summary || {};
  const pppoeDown = data?.pppoe_tidak_aktif || [];
  const redaman = data?.redaman_kritis || [];
  const adaPekerjaan = pppoeDown.length > 0 || redaman.length > 0;

  return (
    <div className="space-y-6" data-testid="teknisi-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-slate-800 shadow-xl">
        <div>
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
            Field Technician Panel
          </span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Dashboard Teknisi Lapangan</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Antrian pekerjaan PPPoE tidak aktif & redaman kritis. Data diperbarui otomatis tiap 1 menit.
          </p>
        </div>
        <button
          onClick={fetchData}
          data-testid="teknisi-refresh-btn"
          className="px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 flex items-center gap-1.5 transition-colors self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* NOTIFIKASI PPPoE TIDAK AKTIF */}
      {!loading && pppoeDown.length > 0 && !dismissed && (
        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/50 shadow-lg shadow-rose-950/30 animate-pulse-slow" data-testid="teknisi-pppoe-alert">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center flex-shrink-0">
              <WifiOff className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-black text-rose-300">
                PERHATIAN: {pppoeDown.length} pelanggan PPPoE tidak aktif!
              </p>
              <p className="text-xs text-rose-200/80 mt-0.5">
                Segera kerjakan: {pppoeDown.slice(0, 3).map((p) => p.nama).join(", ")}
                {pppoeDown.length > 3 ? ` dan ${pppoeDown.length - 3} lainnya` : ""}. Lihat daftar pekerjaan di bawah.
              </p>
            </div>
            <button
              onClick={() => setDismissed(true)}
              data-testid="teknisi-alert-dismiss"
              className="text-rose-400 hover:text-rose-200 p-1"
              title="Sembunyikan notifikasi"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {!loading && !adaPekerjaan && (
        <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/40 flex items-center gap-3" data-testid="teknisi-all-clear">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <p className="text-xs font-semibold text-emerald-300">
            Semua layanan normal — tidak ada PPPoE tidak aktif maupun redaman kritis saat ini.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="teknisi-card-total">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Total Pelanggan</p>
              <h3 className="text-2xl font-black text-white mt-1">{summary.total_pelanggan || 0}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="teknisi-card-aktif">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Layanan Aktif</p>
              <h3 className="text-2xl font-black text-emerald-400 mt-1">{summary.aktif || 0}</h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center">
              <CheckCircle className="w-5 h-5" />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="teknisi-card-isolir">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">PPPoE Tidak Aktif</p>
              <h3 className="text-2xl font-black text-rose-400 mt-1">{(summary.isolir || 0) + (summary.nonaktif || 0)}</h3>
              <p className="text-[10px] text-slate-500 mt-1">{summary.isolir || 0} isolir · {summary.nonaktif || 0} nonaktif</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-400 flex items-center justify-center">
              <WifiOff className="w-5 h-5" />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="teknisi-card-redaman">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Redaman Kritis</p>
              <h3 className="text-2xl font-black text-amber-400 mt-1">{summary.redaman_kritis || 0}</h3>
              <p className="text-[10px] text-slate-500 mt-1">&lt; -25 dBm (aktif)</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center">
              <Signal className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Daftar Pekerjaan: PPPoE Tidak Aktif */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-3">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <Wrench className="w-4 h-4 text-rose-400" />
            <span>Antrian Pekerjaan — PPPoE Tidak Aktif ({pppoeDown.length})</span>
          </h2>
          <Link to="/jaringan/maps" className="text-xs text-cyan-400 hover:underline font-semibold">
            Buka Peta Jaringan →
          </Link>
        </div>
        {pppoeDown.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-3" data-testid="teknisi-empty-pppoe">
            Tidak ada PPPoE tidak aktif. Semua sesi pelanggan berjalan normal.
          </p>
        ) : (
          <div className="space-y-2.5" data-testid="teknisi-pppoe-list">
            {pppoeDown.map((p) => (
              <div
                key={p.id}
                className="p-3.5 rounded-xl bg-slate-900/70 border border-rose-900/40 hover:border-rose-700/60 transition-colors"
                data-testid={`teknisi-job-${p.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-white text-xs">{p.nama}</span>
                      <span className="text-[10px] font-mono text-slate-400">{p.no_layanan}</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          p.status_layanan === "isolir"
                            ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                            : "bg-slate-700/60 text-slate-300 border border-slate-600"
                        }`}
                      >
                        {p.status_layanan}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Router className="w-3 h-3 text-cyan-400" />
                      PPPoE: <span className="font-mono text-cyan-300">{p.username_pppoe}</span>
                      <span className="text-slate-600">•</span> {p.paket_nama || "-"}
                      <span className="text-slate-600">•</span> ODP {p.odp_kode || "-"}
                      {p.odp_port ? ` port ${p.odp_port}` : ""}
                    </p>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-purple-400" />
                      {p.alamat} — {p.wilayah}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => setFotoPel(p)}
                      title="Lihat foto rumah & ODP"
                      data-testid={`teknisi-foto-${p.id}`}
                      className="p-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-cyan-300 rounded-xl transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    {p.no_hp && (
                      <a
                        href={`https://wa.me/${String(p.no_hp).replace(/\D/g, "").replace(/^0/, "62")}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`teknisi-wa-${p.id}`}
                        className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-600/40 text-emerald-300 text-[11px] font-bold rounded-xl flex items-center gap-1.5 transition-colors"
                      >
                        <Phone className="w-3.5 h-3.5" /> {p.no_hp}
                      </a>
                    )}
                    <Link
                      to="/pelanggan"
                      data-testid={`teknisi-open-${p.id}`}
                      className="px-3 py-1.5 bg-cyan-600/20 hover:bg-cyan-600/40 border border-cyan-600/40 text-cyan-300 text-[11px] font-bold rounded-xl transition-colors"
                    >
                      Kerjakan →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {fotoPel && <PelangganFotoDialog pelanggan={fotoPel} onClose={() => setFotoPel(null)} />}

      {/* Redaman Kritis */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-3">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <h2 className="text-sm font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span>Redaman Kritis (&lt; -25 dBm) — Perlu Penarikan/Cek Kabel ({redaman.length})</span>
          </h2>
          <Link to="/jaringan/olt" className="text-xs text-cyan-400 hover:underline font-semibold">
            Monitor OLT →
          </Link>
        </div>
        {redaman.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-3" data-testid="teknisi-empty-redaman">
            Tidak ada redaman kritis pada pelanggan aktif.
          </p>
        ) : (
          <div className="space-y-2.5" data-testid="teknisi-redaman-list">
            {redaman.map((p) => (
              <div
                key={p.id}
                className="p-3.5 rounded-xl bg-slate-900/70 border border-amber-900/40 flex flex-col md:flex-row md:items-center justify-between gap-3"
                data-testid={`teknisi-redaman-${p.id}`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white text-xs">{p.nama}</span>
                    <span className="text-[10px] font-mono text-slate-400">{p.no_layanan}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-950/60 text-amber-400 border border-amber-800">
                      {p.optical_power} dBm
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    SN: {p.onu_sn || "-"} • ODP {p.odp_kode || "-"}{p.odp_port ? ` port ${p.odp_port}` : ""} • {p.wilayah}
                  </p>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-purple-400" /> {p.alamat}
                  </p>
                </div>
                {p.no_hp && (
                  <a
                    href={`https://wa.me/${String(p.no_hp).replace(/\D/g, "").replace(/^0/, "62")}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`teknisi-wa-redaman-${p.id}`}
                    className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-600/40 text-emerald-300 text-[11px] font-bold rounded-xl flex items-center gap-1.5 transition-colors flex-shrink-0"
                  >
                    <Phone className="w-3.5 h-3.5" /> {p.no_hp}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
